import BrandLogo from '@/components/common/BrandLogo';
import { brandConfig } from '@/config/brandConfig';
import { addressTemplatePages, type TemplateSender } from '@/lib/printTemplates';

function WritingLine({ label }: { label?: string }) {
  return (
    <p className="pt-writing-line">
      {label && <span>{label}</span>}
      <span className="pt-rule" />
    </p>
  );
}

export default function AddressTemplateSheets({
  copies,
  perPage,
  sender,
  includeSender,
  recipients,
}: {
  copies: number;
  perPage: 3 | 4;
  sender: TemplateSender;
  includeSender: boolean;
  recipients?: Array<{ name: string; address: string; phone: string; reference: string }>;
}) {
  return (
    <div id="address-template-sheets" style={{ color: '#171b22', fontFamily: 'Arial, sans-serif' }}>
      <style>{`
        .pt-sheet{box-sizing:border-box;width:190mm;height:276mm;display:grid;gap:4mm;background:white;margin:0 auto 16px;print-color-adjust:exact;-webkit-print-color-adjust:exact}
        .pt-label{position:relative;min-height:0;display:flex;flex-direction:column;padding:3.5mm;border:1px solid #7a8490;background:white;gap:2.5mm}
        .pt-label p{margin:0}.pt-label header{display:flex;align-items:center;gap:2.5mm;flex-shrink:0;border-bottom:1px solid #c9ab6c;padding-bottom:2mm}
        .pt-label .pt-brand-name{font-size:16px;font-weight:bold;letter-spacing:.12em}.pt-label .pt-tagline{font-size:10px;color:#4b5563;margin-top:2px}
        .pt-label .pt-kind{margin-left:auto;max-width:30%;text-align:right;font-size:9px;letter-spacing:.08em;color:#4b5563;text-transform:uppercase}
        .pt-label .pt-columns{display:grid;grid-template-columns:2fr 3fr;gap:4mm;flex:1;min-height:0}
        .pt-label .pt-from,.pt-label .pt-to{min-width:0;min-height:0;overflow:hidden}
        .pt-label .pt-from{padding-right:4mm;border-right:1px solid #c6cdd5}
        .pt-label .pt-heading{font-size:11px;font-weight:bold;letter-spacing:.14em;margin-bottom:2mm}
        .pt-label .pt-sender-name{font-size:13px;font-weight:bold;line-height:1.35;overflow-wrap:anywhere}
        .pt-label .pt-sender-address{font-size:12px;line-height:1.45;white-space:pre-wrap;overflow-wrap:anywhere;margin-top:1.5mm}
        .pt-label .pt-sender-phone{font-size:11px;line-height:1.4;overflow-wrap:anywhere;margin-top:2mm}
        .pt-label .pt-writing-line{display:flex;align-items:baseline;gap:2mm;font-size:10px;line-height:1.2;min-height:6.3mm;margin:0;white-space:nowrap}
        .pt-label .pt-rule{flex:1;min-width:0;border-bottom:1px solid #87929e;height:4.5mm}
        .pt-label .pt-contact-row{display:grid;grid-template-columns:3fr 2fr;gap:3mm}
        .pt-cut{position:absolute;bottom:-2.7mm;left:0;right:0;display:flex;align-items:center;gap:2mm;font-size:8px;color:#596675;line-height:1}
        .pt-cut::before,.pt-cut::after{content:'';flex:1;border-top:1px dashed #7a8490}
        @media print{@page{size:A4 portrait;margin:10mm}.pt-sheet{margin:0;break-after:page;page-break-after:always;break-inside:avoid;page-break-inside:avoid}.pt-sheet:last-child{break-after:auto;page-break-after:auto}.pt-label{break-inside:avoid;page-break-inside:avoid}}
      `}</style>
      {addressTemplatePages(copies, perPage).map((labels, pageIndex) => (
        <section
          key={pageIndex}
          className="pt-sheet"
          aria-label={`Address sheet ${pageIndex + 1}`}
          style={{ gridTemplateRows: `repeat(${perPage}, minmax(0, 1fr))` }}
        >
          {labels.map((number, index) => (
            <article className="pt-label" key={number} aria-label={`Address label ${number}`}>
              <header>
                <BrandLogo width={38} height={38} style={{ width: '10mm', height: '10mm' }} />
                <div>
                  <p className="pt-brand-name">{brandConfig.name.toUpperCase()}</p>
                  <p className="pt-tagline">{brandConfig.tagline}</p>
                </div>
                <p className="pt-kind">
                  {recipients?.[number - 1]?.reference || 'Delivery address'}
                </p>
              </header>
              <div className="pt-columns">
                <div className="pt-from">
                  <p className="pt-heading">FROM</p>
                  {includeSender ? (
                    <>
                      <p className="pt-sender-name">{sender.name.trim()}</p>
                      <p className="pt-sender-address">{sender.address.trim()}</p>
                      {sender.phone.trim() && (
                        <p className="pt-sender-phone">Phone: {sender.phone.trim()}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <WritingLine label="Name" />
                      <WritingLine label="Address" />
                      <WritingLine />
                      <WritingLine />
                      <WritingLine label="Phone" />
                    </>
                  )}
                </div>
                <div className="pt-to">
                  <p className="pt-heading">TO</p>
                  {recipients?.[number - 1] ? (
                    <>
                      <p className="pt-sender-name">{recipients[number - 1]!.name}</p>
                      <p className="pt-sender-address">{recipients[number - 1]!.address}</p>
                      {recipients[number - 1]!.phone && (
                        <p className="pt-sender-phone">
                          Phone: <span data-customer-phone>{recipients[number - 1]!.phone}</span>
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <WritingLine label="Name" />
                      <WritingLine label="Address" />
                      <WritingLine />
                      <WritingLine />
                      <div className="pt-contact-row">
                        <WritingLine label="Phone" />
                        <WritingLine label="PIN" />
                      </div>
                    </>
                  )}
                </div>
              </div>
              {index < labels.length - 1 && (
                <div className="pt-cut" aria-hidden="true">
                  Cut here
                </div>
              )}
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}
